<script setup lang="ts">
import { computed, ref, type ComponentPublicInstance, type PropType } from 'vue';
import type {
  ContractManagementContractStates,
  EffectiveState,
  NeedsAttentionReason,
  VaultFileSignatureInfoInDb,
} from '@/api';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaSignatureList from '@/components/design/domain/fileVault/GaiaSignatureList.vue';
import GaiaContractStateIcon from './GaiaContractStateIcon.vue';
import {
  contractIconLayers,
  contractStateLabel,
  needsAttentionReasonLabels,
} from './contractStateIcons';

/**
 * A contract's (or a single file's) state as a table cell: the state icon,
 * its label, and — when an abnormal amendment overlays the contract — an
 * "Amendment: …" line under it. Needs-attention reasons, a failed signing
 * request's reason and detected signatures, when given, open in a tooltip.
 */
const props = defineProps({
  /** A contract's resolved states; takes precedence over `state`. */
  states: {
    type: Object as PropType<ContractManagementContractStates | null>,
    default: null,
  },
  /** One file's or revision's own state, drawn as a contract tile. */
  state: {
    type: String as PropType<EffectiveState | null>,
    default: null,
  },
  reasons: {
    type: Array as PropType<NeedsAttentionReason[] | null>,
    default: null,
  },
  /** Why sending for signature failed (`signature_failed`). */
  failureReason: {
    type: String as PropType<string | null>,
    default: null,
  },
  signatureInfos: {
    type: Array as PropType<VaultFileSignatureInfoInDb[] | null>,
    default: null,
  },
  /** A child row (a contract's file): smaller icon, quieter label. */
  compact: {
    type: Boolean,
    default: false,
  },
});

const layers = computed(() =>
  contractIconLayers(props.states ?? { contract_state: props.state, amendment_state: null })
);
// The amendment alone (no contract file) still has something to show.
const primary = computed(() => layers.value.contract ?? layers.value.amendment);
const primaryLabel = computed(() =>
  layers.value.contract
    ? contractStateLabel(layers.value.contract)
    : `Amendment: ${contractStateLabel(layers.value.amendment)}`
);
const overlaid = computed(() => Boolean(layers.value.contract && layers.value.amendment));

const reasonLabels = computed(() => (props.reasons ?? []).map(r => needsAttentionReasonLabels[r]));
const signatures = computed(() => props.signatureInfos ?? []);
const hasDetail = computed(
  () => reasonLabels.value.length > 0 || Boolean(props.failureReason) || signatures.value.length > 0
);

const iconSize = computed(() => (props.compact ? 20 : 24));

// Labels ellipsize to the column; on hover, show the full text only if it was cut.
const labelText = ref<ComponentPublicInstance | null>(null);
const amendmentText = ref<ComponentPublicInstance | null>(null);
const truncated = ref(false);
function measureTruncation(): void {
  truncated.value = [labelText.value, amendmentText.value].some(text => {
    const el = text?.$el as HTMLElement | undefined;
    return Boolean(el && el.scrollWidth > el.clientWidth);
  });
}
</script>

<template>
  <gaia-text v-if="!primary" tone="blank">—</gaia-text>
  <gaia-tooltip v-else placement="top" :disabled="!hasDetail && !truncated">
    <template #trigger>
      <!-- min-width: 0 lets the row shrink below its label's width (a flex item
           otherwise won't), so the label ellipsizes instead of overflowing. -->
      <n-flex
        align="center"
        :size="8"
        :wrap="false"
        style="min-width: 0; max-width: 100%"
        @mouseenter="measureTruncation"
        @focusin="measureTruncation"
      >
        <!-- A fixed slot as wide as a stacked icon, so labels line up down the
             column whether or not a row has an overlay. -->
        <n-flex align="center" :size="0" :wrap="false" style="flex: none; width: 34px">
          <gaia-contract-state-icon
            :contract="layers.contract"
            :amendment="layers.amendment"
            :size="iconSize"
          />
        </n-flex>
        <n-flex vertical :size="0" :wrap="false" style="min-width: 0">
          <gaia-text
            ref="labelText"
            truncate
            :size="compact ? 'meta' : 'control'"
            :tone="compact ? 'tertiary' : 'text'"
          >
            {{ primaryLabel }}
          </gaia-text>
          <gaia-text v-if="overlaid" ref="amendmentText" truncate size="micro" tone="tertiary">
            Amendment: {{ contractStateLabel(layers.amendment) }}
          </gaia-text>
        </n-flex>
      </n-flex>
    </template>

    <n-flex vertical :size="10" :wrap="false" style="min-width: 220px">
      <n-flex v-if="truncated" vertical :size="2" :wrap="false">
        <gaia-text size="control" weight="medium">{{ primaryLabel }}</gaia-text>
        <gaia-text v-if="overlaid" size="meta">
          Amendment: {{ contractStateLabel(layers.amendment) }}
        </gaia-text>
      </n-flex>
      <gaia-text v-for="label in reasonLabels" :key="label" size="control">{{ label }}</gaia-text>
      <gaia-text v-if="failureReason" size="control">{{ failureReason }}</gaia-text>
      <gaia-signature-list :signatures="signatures" />
    </n-flex>
  </gaia-tooltip>
</template>
